import { Link } from "react-router-dom";
import {
  ArrowRight, Layers, Link2, Smartphone, Shield, Activity,
  Rocket, CheckCircle, Globe, Repeat
} from "lucide-react";

const erpOfferings = [
  {
    icon: Rocket,
    title: "ERP Implementation, Migration & Support",
    items: [
      "End-to-end ERP implementations following industry best practices",
      "ERP modernization including ECC to S/4HANA migrations",
      "Post-go-live AMS integrated with security, compliance, and infrastructure",
      "Secure, audit-ready ERP environments",
    ],
  },
  {
    icon: Layers,
    title: "ERP Integrated Products",
    items: [
      "Extend ERP workflows to sales, warehouse, shopfloor, field service",
      "ERP remains 'system of record' — TEQXL solutions are 'system of action'",
      "Upgrade-safe, clean core design with no direct database writes",
    ],
  },
  {
    icon: Link2,
    title: "Seamless ERP Integration",
    items: [
      "Native SAP S/4HANA, ECC, Business One integration",
      "Compatibility with Oracle ERP, Microsoft Dynamics, Epicor",
      "API-first integration via SAP Service Layer, OData, BAPI, RFC",
    ],
  },
  {
    icon: Smartphone,
    title: "Execution Layer for Real-World Operations",
    items: [
      "Mobile-first applications with online/offline capability",
      "Automatic ERP synchronization",
      "Pre-built workflows for finance, logistics, HR, manufacturing",
    ],
  },
  {
    icon: Shield,
    title: "Governance & Compliance",
    items: [
      "Role-based access aligned to ERP governance",
      "Audit trails and approval workflows",
      "Compliance with GDPR, SOX, HIPAA, regional tax frameworks",
    ],
  },
  {
    icon: Activity,
    title: "Monitoring & Optimization",
    items: [
      "24x7 ERP landscape monitoring",
      "SLA-driven incident management",
      "Proactive patching, upgrades, and performance tuning",
    ],
  },
];

const erpBenefits = [
  { icon: CheckCircle, text: "Clean core, upgrade-safe ERP environments" },
  { icon: Rocket, text: "Faster execution with reduced go-live effort" },
  { icon: Repeat, text: "Lower customization and manual effort" },
  { icon: Globe, text: "Scalable multi-entity, multi-country deployments" },
  { icon: Activity, text: "Improved ROI through real-world ERP execution" },
];

export default function ERPServicePage() {
  return (
    <div data-testid="erp-service-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
                ERP Operations & Management
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
                Integrated Products for Enterprise Execution
              </h1>
              <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-8">
                We deliver ERP and ERP-integrated products that extend ERP workflows into
                real-world execution environments without modifying the ERP core. Our solutions
                integrate seamlessly across leading ERP platforms, ensuring scalability,
                compliance, and innovation.
              </p>
              <Link
                to="/contact"
                data-testid="erp-hero-cta"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors"
              >
                Explore ERP Solutions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="overflow-hidden lg:overflow-visible">
              <img
                src="/erp-operations.png"
                alt="ERP Operations"
                className="w-full h-auto lg:h-[400px] lg:object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ERP Offerings */}
      <section data-testid="erp-offerings-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              What We Deliver
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              End-to-end ERP solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200 bg-white">
            {erpOfferings.map((offering, i) => (
              <div
                key={offering.title}
                data-testid={`erp-offering-${i}`}
                className={`p-8 lg:p-10 border-b border-gray-200 last:border-b-0 ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""
                } ${(i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0 border-gray-200" : ""}`}
              >
                <offering.icon className="w-6 h-6 text-[#002FA7] mb-5" />
                <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-4">
                  {offering.title}
                </h3>
                <ul className="space-y-2">
                  {offering.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#4B5563]">
                      <span className="w-1 h-1 rounded-full bg-[#002FA7] mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ERP Benefits */}
      <section data-testid="erp-benefits-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
                Business Benefits
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                Maximize your ERP investment
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                Our ERP solutions are designed to maintain clean core integrity while
                delivering faster execution, lower customization, and scalable deployments
                across multiple entities and countries.
              </p>
            </div>
            <div className="border border-gray-200">
              {erpBenefits.map((b, i) => (
                <div
                  key={b.text}
                  className={`flex items-center gap-4 p-6 ${
                    i < erpBenefits.length - 1 ? "border-b border-gray-200" : ""
                  }`}
                >
                  <b.icon className="w-5 h-5 text-[#002FA7] shrink-0" />
                  <p className="text-sm text-[#0A0A0A]">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6">
            Extend your ERP into real-world execution
          </h2>
          <p className="text-base text-blue-200 max-w-xl mx-auto mb-10 leading-relaxed">
            Let TEQXL bridge the gap between your ERP system of record and
            the real-world operations that drive your business.
          </p>
          <Link
            to="/contact"
            data-testid="erp-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Talk to Our ERP Team <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
